import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Actions from '../firebase/actions';
import { AppModuleId, DashboardBoardLayout, DashboardGridLayoutItem, DashboardModuleColor, DashboardModuleSpan, SidebarLayout } from '../types';
import {
  buildDefaultDashboardBoardLayout,
  getAppModuleDefinition,
  getSpanFromDefaultSize,
  normalizeDashboardBoardLayout,
  serializeDashboardGridLayoutItems,
} from '../utils/appRegistry';
import { useFirestoreState } from './useFirestore';

function cloneBoardLayout(layout: DashboardBoardLayout): DashboardBoardLayout {
  return {
    order: [...(layout.order ?? [])],
    gridLayouts: {
      xl: Array.isArray(layout.gridLayouts?.xl) ? layout.gridLayouts.xl.map((item) => ({ ...item })) : undefined,
    },
    placements: Object.fromEntries(
      Object.entries(layout.placements ?? {}).map(([placementId, placement]) => [placementId, { ...placement }])
    ),
  };
}

function areGridLayoutItemsEqual(left: DashboardGridLayoutItem[] | undefined, right: DashboardGridLayoutItem[] | undefined) {
  const normalizedLeft = serializeDashboardGridLayoutItems(left ?? []);
  const normalizedRight = serializeDashboardGridLayoutItems(right ?? []);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((item, index) => {
    const target = normalizedRight[index];
    return (
      item.i === target?.i &&
      item.x === target?.x &&
      item.y === target?.y &&
      item.w === target?.w &&
      item.h === target?.h
    );
  });
}

function arePlacementsEqual(left: DashboardBoardLayout['placements'], right: DashboardBoardLayout['placements']) {
  const leftEntries = Object.entries(left ?? {}).sort(([leftId], [rightId]) => leftId.localeCompare(rightId));
  const rightEntries = Object.entries(right ?? {}).sort(([leftId], [rightId]) => leftId.localeCompare(rightId));

  if (leftEntries.length !== rightEntries.length) {
    return false;
  }

  return leftEntries.every(([placementId, placement], index) => {
    const [targetPlacementId, target] = rightEntries[index] ?? [];

    return (
      placementId === targetPlacementId &&
      placement.moduleId === target?.moduleId &&
      placement.order === target?.order &&
      placement.visible === target?.visible &&
      placement.moduleColor === target?.moduleColor &&
      placement.span === target?.span
    );
  });
}

const useDashboardBoard = () => {
  const storedLayoutState = useFirestoreState<DashboardBoardLayout | null>('crypto-missions:dashboardBoardLayout', null);
  const legacyLayoutState = useFirestoreState<SidebarLayout | null>('crypto-missions:sidebarLayout', null);
  const storedLayout = storedLayoutState.value;
  const legacyLayout = legacyLayoutState.value;
  const [optimisticLayout, setOptimisticLayout] = useState<DashboardBoardLayout | null>(null);
  const isHydrated = storedLayoutState.isHydrated && legacyLayoutState.isHydrated;
  const hasStoredLayout = storedLayoutState.hasRemoteValue && storedLayout != null;
  const hasLegacyLayout = legacyLayoutState.hasRemoteValue && legacyLayout != null;

  useEffect(() => {
    if (storedLayout) {
      setOptimisticLayout(null);
    }
  }, [storedLayout]);

  const layout = useMemo(() => {
    const candidateLayout = optimisticLayout ?? storedLayout;
    if (!isHydrated && !optimisticLayout) {
      return buildDefaultDashboardBoardLayout();
    }

    if (candidateLayout) {
      return normalizeDashboardBoardLayout(candidateLayout, hasLegacyLayout ? legacyLayout : undefined);
    }

    return normalizeDashboardBoardLayout(
      hasStoredLayout ? storedLayout : undefined,
      hasLegacyLayout ? legacyLayout : undefined
    );
  }, [hasLegacyLayout, hasStoredLayout, isHydrated, legacyLayout, optimisticLayout, storedLayout]);

  const placedModuleIds = useMemo(() => {
    const moduleIds = new Set<AppModuleId>();
    Object.values(layout.placements).forEach((placement) => {
      if (placement.visible) {
        moduleIds.add(placement.moduleId);
      }
    });
    return Array.from(moduleIds);
  }, [layout.placements]);

  const persistLayout = useCallback(async (nextLayout: DashboardBoardLayout) => {
    const normalized = normalizeDashboardBoardLayout(nextLayout, legacyLayout);
    if (
      normalized.order.join('|') === layout.order.join('|') &&
      areGridLayoutItemsEqual(normalized.gridLayouts?.xl, layout.gridLayouts?.xl) &&
      arePlacementsEqual(normalized.placements, layout.placements)
    ) {
      return;
    }

    setOptimisticLayout(normalized);

    try {
      await Actions.handleDashboardBoardLayoutChange(normalized);
    } catch (error) {
      console.error('Failed to persist dashboard board layout:', error);
      setOptimisticLayout(null);
      throw error;
    }
  }, [layout, legacyLayout]);

  const addModule = useCallback(async (moduleId: AppModuleId) => {
    const nextLayout = cloneBoardLayout(layout);
    const placementId = moduleId;

    if (nextLayout.placements[placementId]?.visible) {
      return;
    }

    if (!nextLayout.order.includes(placementId)) {
      nextLayout.order.push(placementId);
    }

    const definition = getAppModuleDefinition(moduleId);
    if (!definition) {
      return;
    }

    nextLayout.placements[placementId] = {
      ...nextLayout.placements[placementId],
      placementId,
      moduleId,
      order: nextLayout.order.length - 1,
      visible: true,
      moduleColor: nextLayout.placements[placementId]?.moduleColor ?? 'default',
      span: nextLayout.placements[placementId]?.span ?? getSpanFromDefaultSize(definition.defaultSize),
    };

    await persistLayout(nextLayout);
  }, [layout, persistLayout]);

  const removeModule = useCallback(async (moduleId: AppModuleId) => {
    const nextLayout = cloneBoardLayout(layout);
    const placementId = moduleId;
    const existing = nextLayout.placements[placementId];
    if (!existing) return;

    nextLayout.order = nextLayout.order.filter((id) => id !== placementId);
    nextLayout.placements[placementId] = {
      ...existing,
      visible: false,
      order: -1,
    };

    await persistLayout(nextLayout);
  }, [layout, persistLayout]);

  const setModuleColor = useCallback(async (placementId: string, moduleColor: DashboardModuleColor) => {
    const placement = layout.placements[placementId];
    if (!placement || placement.moduleColor === moduleColor) return;

    const nextLayout = cloneBoardLayout(layout);
    nextLayout.placements[placementId] = {
      ...placement,
      moduleColor,
    };
    await persistLayout(nextLayout);
  }, [layout, persistLayout]);

  const setModuleSpan = useCallback(async (placementId: string, span: DashboardModuleSpan) => {
    const placement = layout.placements[placementId];
    if (!placement || placement.span === span) return;

    const nextLayout = cloneBoardLayout(layout);
    nextLayout.placements[placementId] = {
      ...placement,
      span,
    };
    await persistLayout(nextLayout);
  }, [layout, persistLayout]);

  const saveGridLayoutXl = useCallback(async (items: DashboardGridLayoutItem[]) => {
    const visibleItems = items.filter((item) => Boolean(layout.placements[item.i]?.visible));
    if (areGridLayoutItemsEqual(visibleItems, layout.gridLayouts?.xl)) {
      return;
    }

    const nextLayout = cloneBoardLayout(layout);
    nextLayout.gridLayouts = {
      ...nextLayout.gridLayouts,
      xl: serializeDashboardGridLayoutItems(visibleItems),
    };
    await persistLayout(nextLayout);
  }, [layout, persistLayout]);

  const resetBoard = useCallback(async () => {
    await persistLayout(buildDefaultDashboardBoardLayout());
  }, [persistLayout]);

  return {
    isHydrated,
    hasRemoteValue: storedLayoutState.hasRemoteValue,
    error: storedLayoutState.error ?? legacyLayoutState.error,
    layout,
    placedModuleIds,
    addModule,
    removeModule,
    setModuleColor,
    setModuleSpan,
    saveGridLayoutXl,
    resetBoard,
  };
};

export default useDashboardBoard;
